import { inject, Injectable } from '@angular/core';
import { LocalStorageService } from '@xmedical/core';
import { PatientFormValue } from './patient-form.service';

// A persisted draft has the same shape as the live form value.
export type PatientDraft = PatientFormValue;

export interface DraftKeyContext {
  practiceId: number | string | null | undefined;
  userId: string | null | undefined;
}

// Persists in-progress new-patient form data to localStorage so a tab refresh
// or accidental navigation does not lose user input. Edit-mode patients are
// not persisted — caller decides whether to call save().
@Injectable({ providedIn: 'root' })
export class PatientDraftPersistence {

  private storage = inject(LocalStorageService);

  save(value: PatientFormValue, ctx: DraftKeyContext): void {
    this.storage.setItem(this.key(ctx), value);
  }

  load(ctx: DraftKeyContext): PatientDraft | null {
    const raw = this.storage.getItem(this.key(ctx));
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    return this.normalise(raw);
  }

  clear(ctx: DraftKeyContext): void {
    this.storage.deleteItem(this.key(ctx));
  }

  private key(ctx: DraftKeyContext): string {
    const practiceId = ctx.practiceId ?? 'unknown';
    const userId = ctx.userId ?? 'unknown';
    return `${practiceId}-${userId}-new-patient`;
  }

  // Convert raw stored data into a clean PatientDraft:
  // - Replace nulls/undefined scalars with '' so reactive controls accept them
  //   (round-trip from older drafts saved under the legacy Untyped* defaults).
  // - Filter array entries that lack a usable `type` + `label` so the form
  //   keeps its sensible defaults rather than rendering empty/garbage rows.
  private normalise(raw: any): PatientDraft {
    const scalarised = nullsToEmpty(raw) as Record<string, any>;

    const contactNumbers = filterEntries(scalarised.contactNumbers);
    const emailAddresses = filterEntries(scalarised.emailAddresses);
    const addressDetails = filterEntries(scalarised.addressDetails);

    const billing = scalarised.billingContact ?? {};
    const billingContactContactNumbers = filterEntries(billing.billingContactContactNumbers);
    const billingContactEmailAddresses = filterEntries(billing.billingContactEmailAddresses);
    const billingContactAddressDetails = filterEntries(billing.billingContactAddressDetails);

    return {
      ...scalarised,
      contactNumbers,
      emailAddresses,
      addressDetails,
      billingContact: {
        ...billing,
        billingContactContactNumbers,
        billingContactEmailAddresses,
        billingContactAddressDetails,
      },
    } as PatientDraft;
  }
}

// Recursively replace null/undefined with '' so legacy drafts round-trip.
// Arrays preserve length; nested objects recurse; primitives pass through.
function nullsToEmpty(value: unknown): unknown {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.map(nullsToEmpty);
  }
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const k of Object.keys(value as object)) {
      result[k] = nullsToEmpty((value as Record<string, unknown>)[k]);
    }
    return result;
  }
  return value;
}

// An entry is usable only if it has a numeric type and a non-empty label —
// the form's add* methods always produce both, so anything missing them is
// stale or partial data and is dropped.
function filterEntries(arr: unknown): any[] {
  if (!Array.isArray(arr)) {
    return [];
  }
  return arr.filter(item =>
    item &&
    typeof item === 'object' &&
    (item as any).type !== '' &&
    (item as any).type !== null &&
    (item as any).type !== undefined &&
    (item as any).label !== '' &&
    (item as any).label !== null &&
    (item as any).label !== undefined,
  );
}
